/**
 * Scene Splitting Hooks
 * Feature: Instagram Reel Scene Splitting
 *
 * Provides hooks for:
 * 1. Scene splitting orchestration (useSceneSplitting)
 * 2. Individual scene editing (useSceneEditor)
 * 3. Scene data fetching (useScenesByReel)
 */

import { useReducer, useCallback, useMemo } from 'react';
import { useAction, useQuery, useMutation } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type { ModelKey } from '../@types/reels';
import type {
  IReelScene,
  ISceneSplittingState,
  ISceneEditorState,
  SceneSplittingAction,
  SceneEditorAction,
  ISceneEditPayload,
  ActiveSceneType,
  IUseSceneSplittingReturn,
  IUseSceneEditorReturn,
} from '../@types/reelScenes';

// ============================================================================
// Scene Splitting State Management
// ============================================================================

/**
 * Initial state for scene splitting
 */
const initialSplittingState: ISceneSplittingState = {
  phase: 'idle',
  scenes: [],
  totalScenes: 0,
  isLoading: false,
  isSplitting: false,
  error: null,
  selectedModelKey: 'claude',
};

/**
 * Scene Splitting Reducer
 * Handles all state transitions for the scene splitting flow
 */
function sceneSplittingReducer(
  state: ISceneSplittingState,
  action: SceneSplittingAction
): ISceneSplittingState {
  switch (action.type) {
    case 'START_SPLIT':
      return {
        ...state,
        phase: 'splitting',
        isSplitting: true,
        error: null,
      };

    case 'SPLIT_SUCCESS':
      return {
        ...state,
        phase: 'split',
        isSplitting: false,
        scenes: action.payload.scenes,
        totalScenes: action.payload.totalScenes,
        error: null,
      };

    case 'SPLIT_ERROR':
      return {
        ...state,
        phase: 'error',
        isSplitting: false,
        error: action.payload.error,
      };

    case 'SET_MODEL':
      return {
        ...state,
        selectedModelKey: action.payload.modelKey,
      };

    case 'SCENES_LOADED':
      return {
        ...state,
        phase: action.payload.scenes.length > 0 ? 'split' : 'idle',
        scenes: action.payload.scenes,
        totalScenes: action.payload.scenes.length,
        isLoading: false,
      };

    case 'SCENE_UPDATED': {
      const updatedScenes = state.scenes.map(scene =>
        scene._id === action.payload.scene._id ? action.payload.scene : scene
      );
      return {
        ...state,
        scenes: updatedScenes,
      };
    }

    case 'SCENE_DELETED': {
      const filteredScenes = state.scenes.filter(scene => scene._id !== action.payload.sceneId);
      return {
        ...state,
        scenes: filteredScenes,
        totalScenes: filteredScenes.length,
      };
    }

    case 'SCENES_REORDERED': {
      // Reorder scenes based on the new order
      const orderedScenes = action.payload.sceneOrder
        .map(id => state.scenes.find(s => s._id === id))
        .filter((s): s is IReelScene => s !== undefined)
        .map((scene, index) => ({
          ...scene,
          sceneNumber: index + 1,
        }));
      return {
        ...state,
        scenes: orderedScenes,
      };
    }

    case 'RESET':
      return initialSplittingState;

    default:
      return state;
  }
}

// ============================================================================
// Scene Editor State Management
// ============================================================================

/**
 * Initial state for scene editor
 */
const initialEditorState: ISceneEditorState = {
  editingSceneId: null,
  mode: 'view',
  pendingChanges: null,
  isSaving: false,
  isRegenerating: false,
  regeneratingSceneId: null,
  error: null,
};

/**
 * Scene Editor Reducer
 * Handles state transitions for individual scene editing
 */
function sceneEditorReducer(
  state: ISceneEditorState,
  action: SceneEditorAction
): ISceneEditorState {
  switch (action.type) {
    case 'START_EDIT':
      return {
        ...state,
        editingSceneId: action.payload.sceneId,
        mode: 'edit',
        pendingChanges: {},
        error: null,
      };

    case 'CANCEL_EDIT':
      return {
        ...state,
        editingSceneId: null,
        mode: 'view',
        pendingChanges: null,
        error: null,
      };

    case 'UPDATE_PENDING':
      return {
        ...state,
        pendingChanges: {
          ...state.pendingChanges,
          ...action.payload,
        },
      };

    case 'START_SAVE':
      return {
        ...state,
        isSaving: true,
        error: null,
      };

    case 'SAVE_SUCCESS':
      return {
        ...state,
        isSaving: false,
        editingSceneId: null,
        mode: 'view',
        pendingChanges: null,
        error: null,
      };

    case 'SAVE_ERROR':
      return {
        ...state,
        isSaving: false,
        error: action.payload.error,
      };

    case 'START_REGENERATE':
      return {
        ...state,
        isRegenerating: true,
        regeneratingSceneId: action.payload.sceneId,
        error: null,
      };

    case 'REGENERATE_SUCCESS':
      return {
        ...state,
        isRegenerating: false,
        regeneratingSceneId: null,
        error: null,
      };

    case 'REGENERATE_ERROR':
      return {
        ...state,
        isRegenerating: false,
        regeneratingSceneId: null,
        error: action.payload.error,
      };

    case 'RESET':
      return initialEditorState;

    default:
      return state;
  }
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * useScenesByReel Hook
 * Fetches scenes for a reel with real-time updates
 */
function useScenesByReel(reelId: Id<'reels'> | undefined) {
  const scenes = useQuery(api.reelScenes.getScenesByReelId, reelId ? { reelId } : 'skip');

  return {
    scenes: scenes ?? [],
    isLoading: scenes === undefined,
    hasScenes: (scenes?.length ?? 0) > 0,
  };
}

/**
 * useSceneSplitting Hook
 * Orchestrates the scene splitting flow
 */
export function useSceneSplitting(reelId: Id<'reels'> | undefined): IUseSceneSplittingReturn {
  const [state, dispatch] = useReducer(sceneSplittingReducer, initialSplittingState);

  // Fetch existing scenes
  const { scenes, isLoading } = useScenesByReel(reelId);

  // Update state when scenes are loaded
  useMemo(() => {
    if (!isLoading && scenes) {
      dispatch({
        type: 'SCENES_LOADED',
        payload: { scenes: scenes as IReelScene[] },
      });
    }
  }, [scenes, isLoading]);

  // Actions
  const splitReelAction = useAction(api.ai.sceneSplitting.splitReelIntoScenes);

  /**
   * Split Reel into Scenes
   */
  const splitReel = useCallback(
    async (modelKey?: ModelKey) => {
      if (!reelId) return;

      const selectedModel = modelKey ?? state.selectedModelKey;

      dispatch({ type: 'START_SPLIT' });

      try {
        const result = await splitReelAction({
          reelId,
          modelKey: selectedModel,
        });

        if (result.success) {
          // Scenes will be loaded via the query subscription
          dispatch({
            type: 'SPLIT_SUCCESS',
            payload: {
              scenes: [], // Will be populated by query
              totalScenes: result.totalScenes,
            },
          });
          toast.success(`Successfully split into ${result.totalScenes} scenes`);
        } else {
          dispatch({
            type: 'SPLIT_ERROR',
            payload: { error: result.error || 'Scene splitting failed' },
          });
          toast.error(result.error || 'Failed to split scenes');
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Scene splitting failed';
        dispatch({
          type: 'SPLIT_ERROR',
          payload: { error: errorMessage },
        });
        toast.error(errorMessage);
      }
    },
    [reelId, state.selectedModelKey, splitReelAction]
  );

  /**
   * Set Model for Splitting
   */
  const setModel = useCallback((modelKey: ModelKey) => {
    dispatch({ type: 'SET_MODEL', payload: { modelKey } });
  }, []);

  /**
   * Reset State
   */
  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // Computed values
  const canSplit = !!reelId && !state.isSplitting;
  const hasScenes = state.scenes.length > 0;

  return {
    state: {
      ...state,
      scenes: (scenes as IReelScene[]) ?? state.scenes,
      isLoading,
    },
    splitReel,
    setModel,
    reset,
    canSplit,
    hasScenes,
  };
}

/**
 * useSceneEditor Hook
 * Manages individual scene editing, deletion, and regeneration
 */
export function useSceneEditor(reelId: Id<'reels'> | undefined): IUseSceneEditorReturn {
  const [state, dispatch] = useReducer(sceneEditorReducer, initialEditorState);

  // Mutations
  const updateSceneMutation = useMutation(api.reelScenes.updateScene);
  const deleteSceneMutation = useMutation(api.reelScenes.deleteScene);
  const reorderScenesMutation = useMutation(api.reelScenes.reorderScenes);

  // Actions
  const regenerateSceneAction = useAction(api.ai.sceneSplitting.regenerateSingleScene);

  /**
   * Start Editing a Scene
   */
  const startEdit = useCallback((sceneId: Id<'reelScenes'>) => {
    dispatch({ type: 'START_EDIT', payload: { sceneId } });
  }, []);

  /**
   * Cancel Editing
   */
  const cancelEdit = useCallback(() => {
    dispatch({ type: 'CANCEL_EDIT' });
  }, []);

  /**
   * Update Pending Changes
   */
  const updatePending = useCallback((changes: Partial<ISceneEditPayload>) => {
    dispatch({ type: 'UPDATE_PENDING', payload: changes });
  }, []);

  /**
   * Save Scene Changes
   */
  const saveScene = useCallback(
    async (sceneId: Id<'reelScenes'>, changes: ISceneEditPayload) => {
      dispatch({ type: 'START_SAVE' });

      try {
        const result = await updateSceneMutation({
          sceneId,
          editedVoiceoverText: changes.editedVoiceoverText,
          editedSceneDescription: changes.editedSceneDescription,
          editedImagePrompt: changes.editedImagePrompt,
          editedSceneType: changes.editedSceneType,
          // Reference Image Support (AI Avatar Feature)
          useReferenceImage: changes.useReferenceImage,
          referencePersonId: changes.referencePersonId,
          libraryImageId: changes.libraryImageId,
        });

        if (result.success) {
          dispatch({ type: 'SAVE_SUCCESS' });
          toast.success('Scene saved successfully');
        } else {
          dispatch({
            type: 'SAVE_ERROR',
            payload: { error: result.error || 'Failed to save scene' },
          });
          toast.error(result.error || 'Failed to save scene');
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to save scene';
        dispatch({
          type: 'SAVE_ERROR',
          payload: { error: errorMessage },
        });
        toast.error(errorMessage);
      }
    },
    [updateSceneMutation]
  );

  /**
   * Delete Scene
   */
  const deleteScene = useCallback(
    async (sceneId: Id<'reelScenes'>) => {
      try {
        const result = await deleteSceneMutation({ sceneId });

        if (result.success) {
          toast.success('Scene deleted');
        } else {
          dispatch({
            type: 'SAVE_ERROR',
            payload: { error: result.error || 'Failed to delete scene' },
          });
          toast.error(result.error || 'Failed to delete scene');
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to delete scene';
        dispatch({
          type: 'SAVE_ERROR',
          payload: { error: errorMessage },
        });
        toast.error(errorMessage);
      }
    },
    [deleteSceneMutation]
  );

  /**
   * Regenerate Scene
   */
  const regenerateScene = useCallback(
    async (sceneId: Id<'reelScenes'>, sceneType?: ActiveSceneType, modelKey?: ModelKey) => {
      dispatch({ type: 'START_REGENERATE', payload: { sceneId } });

      try {
        const result = await regenerateSceneAction({
          sceneId,
          requestedSceneType: sceneType,
          modelKey,
        });

        if (result.success) {
          dispatch({ type: 'REGENERATE_SUCCESS' });
          toast.success('Scene regenerated with new visuals');
        } else {
          dispatch({
            type: 'REGENERATE_ERROR',
            payload: { error: result.error || 'Failed to regenerate scene' },
          });
          toast.error(result.error || 'Failed to regenerate scene');
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to regenerate scene';
        dispatch({
          type: 'REGENERATE_ERROR',
          payload: { error: errorMessage },
        });
        toast.error(errorMessage);
      }
    },
    [regenerateSceneAction]
  );

  /**
   * Reorder Scenes
   */
  const reorderScenes = useCallback(
    async (sceneOrder: Id<'reelScenes'>[]) => {
      if (!reelId) return;

      try {
        const result = await reorderScenesMutation({
          reelId,
          sceneOrder,
        });

        if (!result.success) {
          dispatch({
            type: 'SAVE_ERROR',
            payload: { error: result.error || 'Failed to reorder scenes' },
          });
        }
      } catch (error) {
        dispatch({
          type: 'SAVE_ERROR',
          payload: {
            error: error instanceof Error ? error.message : 'Failed to reorder scenes',
          },
        });
      }
    },
    [reelId, reorderScenesMutation]
  );

  /**
   * Reset State
   */
  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // Computed values
  const isEditing = state.editingSceneId !== null;
  const canSave =
    isEditing && state.pendingChanges !== null && Object.keys(state.pendingChanges).length > 0;

  return {
    state,
    startEdit,
    cancelEdit,
    saveScene,
    deleteScene,
    regenerateScene,
    updatePending,
    reorderScenes,
    reset,
    isEditing,
    canSave,
  };
}
